import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import ChipGroup from '@/components/inputs/ChipGroup';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import { BackgroundChecksData, backgroundChecksResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

interface NpoBackgroundChecksFormProps {
  nonProfitOrg: NonProfitOrg | undefined;
  isLoading: boolean;
  showBackButton?: boolean;
  showSaveAndContinueButton?: boolean;
  submitLabel?: string;
  onSubmitTapped: (data: BackgroundChecksData) => void;
}

const NpoBackgroundChecksForm = ({
  nonProfitOrg,
  isLoading,
  showBackButton = true,
  showSaveAndContinueButton = true,
  submitLabel = i18n.t('mix.next'),
  onSubmitTapped
}: NpoBackgroundChecksFormProps) => {
  const initialData = {
    employees: nonProfitOrg?.employees,
    volunteers: nonProfitOrg?.volunteers
  };

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount }
  } = useForm<BackgroundChecksData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: backgroundChecksResolver
  });

  const onSubmit: SubmitHandler<BackgroundChecksData> = async (data) => {
    onSubmitTapped(data);
  };

  return (
    <FormWrapper scrollEnable={false}>
      <View style={styles.container}>
        <View style={styles.fields}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.background_checks.title')}
            description={i18n.t('sign_in.nonProfit.background_checks.description')}>
            <View style={styles.chipsContainer}>
              <ChipGroup
                control={control}
                name="employees"
                groupLabel={i18n.t('sign_in.nonProfit.background_checks.employees')}
                options={[
                  { label: capitalize(i18n.t('mix.yes')), value: true },
                  { label: capitalize(i18n.t('mix.no')), value: false }
                ]}
              />

              <ChipGroup
                control={control}
                name="volunteers"
                groupLabel={i18n.t('sign_in.nonProfit.background_checks.volunteers')}
                options={[
                  { label: capitalize(i18n.t('mix.yes')), value: true },
                  { label: capitalize(i18n.t('mix.no')), value: false }
                ]}
              />
            </View>
          </OnboardingSection>
        </View>

        <View style={styles.buttonsContainer}>
          <View style={styles.nextBackContainer}>
            {showBackButton && router.canGoBack() && (
              <BrandedButton disabled={isLoading} style={styles.flexOne} onPress={router.back}>
                {capitalize(i18n.t('mix.back'))}
              </BrandedButton>
            )}
            <BrandedButton
              style={styles.flexOne}
              loading={isLoading}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {submitLabel}
            </BrandedButton>
          </View>
          {showSaveAndContinueButton && <SaveAndContinueLaterButton disabled={isLoading} />}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  fields: {
    flexDirection: 'column',
    gap: 25
  },

  chipsContainer: {
    width: '50%',
    flexDirection: 'column',
    gap: 25
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column',
    gap: 10
  }
});

export default NpoBackgroundChecksForm;
